/// <amd-module name="VC/Common/Utils/Search" />
/**
 * Creates a new debounced version of the function which will postpone its execution milliseconds.
 * While debouncing, a new call will cancel previous one and restart delay.
 * This is slightly different from OfficeFabric.Async.debounce, where previous calls are not canceled
 * but they share the return value of the last call.
 */
export function debouncePromiseFunction<T extends Function>(fn: T, ms: number, valueIfCancel: {}): T {
    let timeout: number | undefined;
    let resolveCurrent: Function | undefined;

    return function (...args: any[]) {
        if (timeout) {
            clearTimeout(timeout);
            resolveCurrent && resolveCurrent(valueIfCancel);
            resolveCurrent = undefined;
            timeout = undefined;
        }

        return new Promise(resolve => {
            resolveCurrent = resolve;
            timeout = setTimeout(() => {
                resolveCurrent = undefined;
                timeout = undefined;
                resolve(fn.apply(null, args));
            }, ms);
        });
    } as any;
}

/**
 * Remember the last results, and if new text is strictly stronger,
 * filter in memory and avoid calling the expensive function.
 * Clarification: "123" is strictly stronger than "12" and than "23", but not than "13".
 * @param filterInMemory A function that filters results in memory.
 * @param canReuseResult A function to identify whether results were complete, or partial (so cannot reuse).
 */
export function reuseIfStrictlyStronger<TResult>(
    fn: SearchType<TResult>,
    filterInMemory: (searchText: string, previousResult: TResult) => TResult,
    canReuseResult: (result: TResult) => boolean,
): SearchType<TResult> {
    let lastSearchText: string;
    let lastPromise: Promise<TResult | undefined>;

    return (searchText, continuationToken) => {
        const doSearch = () => {
            lastSearchText = searchText;
            lastPromise = fn(searchText, continuationToken);
            return lastPromise;
        };

        if (isStrictlyStronger(searchText, lastSearchText)) {
            return lastPromise.then(result => {
                if (result && canReuseResult(result)) {
                    return filterInMemory(searchText, result);
                } else {
                    return doSearch();
                }
            })
            .catch(doSearch);
        }

        return doSearch();
    };
}

function isStrictlyStronger(a: string, b: string): boolean {
    return Boolean(a && b && a.toLowerCase().indexOf(b.toLowerCase()) >= 0);
}

export function createValidator<TResult>(fn: SearchType<TResult>, validate: (searchText: string) => void): SearchType<TResult> {
    return async (searchText, continuationToken) => {
        await validate(searchText);

        return await fn(searchText, continuationToken);
    };
}

export type SearchType<TResult> = (searchText: string, continuationToken: string) => Promise<TResult | undefined>;
export type SearchPivotType<TResult> = (searchText: string, pivotKey: string, continuationToken: string) => Promise<TResult | undefined>;

export function composeSearchByPivot<TResult>(
    functions: { [pivotKey: string]: SearchType<TResult> },
    defaultPivotKey: string,
): SearchPivotType<TResult> {
    return (searchText, pivotKey, continuationToken) =>
        functions[pivotKey || defaultPivotKey](searchText, continuationToken);
}